import type { CdmEvent } from "@/lib/sse/useSSE";
import type { GraphState, Node, Edge, NodeAccent, NodeState } from "./types";
import { buildSeed } from "./seed";

/**
 * Apply a single CDM event to the graph state. Pure (operates on a copy of
 * `prev` returned to the caller). Used both for live SSE streams and for
 * deterministic replays.
 *
 * The mapping from CDM event → graph mutation is deliberately the same shape
 * as the OrbitraOS event sourcing model: graph state is a derived projection
 * of the event log, never a separately-stored truth.
 */
// `now` here is a monotonic clock value used to drive the post-event glow
// pulse. It MUST share the same time base as InterconnectedWeb's per-frame
// `performance.now()` reading — otherwise the diff is in the millions and
// the halo radius blows up to "fill everything" inside Chromium. See bug
// reproduced May 2026 on screen 6.1.
export function applyEvent(prev: GraphState, ev: CdmEvent, now = performance.now()): GraphState {
  const nodes = new Map(prev.nodes);
  const edges = new Map(prev.edges);
  let { centrePair, fullBleed } = prev;

  const upsertNode = (id: string, patch: Partial<Node> & Pick<Node, "kind" | "label" | "state" | "accent">) => {
    const existing = nodes.get(id);
    nodes.set(id, {
      id,
      x: 0, y: 0, vx: 0, vy: 0, pulseUntil: 0,
      ...existing,
      ...patch,
    });
  };

  const igniteNode = (id: string, state: NodeState, accent: NodeAccent) => {
    const n = nodes.get(id);
    if (!n) return;
    nodes.set(id, { ...n, state, accent, pulseUntil: now + 1500 });
  };

  const upsertEdge = (key: string, patch: Partial<Edge> & Pick<Edge, "kind" | "from" | "to" | "state">) => {
    const existing = edges.get(key);
    edges.set(key, {
      id: key,
      weight: 0.4,
      hcsAnchors: [],
      ...existing,
      ...patch,
    });
  };

  switch (ev.eventType) {
    case "scenario.Started": {
      // Starting any scenario clears the Web full-bleed takeover. Without
      // this, playing final-reveal (which emits ui.WebTakeoverEntered) and
      // then restarting T1 leaves the rail covering the viewport — there's
      // no scenario that emits ui.WebTakeoverExited, so the flag was sticky.
      const sid = (ev.payload?.scenarioId as string | undefined) ?? "";
      // For the final-reveal scenario itself we keep fullBleed off here;
      // its own ui.WebTakeoverEntered event re-flips it 400 ms later.
      fullBleed = false;
      // For T1/T2/T3 starts, additionally re-seed the graph topology so
      // a replay (T1 → T2 → T3 → final → T1 again) starts visually clean
      // rather than carrying the previous run's edges + bank nodes + LC
      // nodes. Final-reveal preserves the network state because its job
      // is to reveal it.
      if (sid === "t1-onboarding" || sid === "t2-bank-financed" || sid === "t3-reverse-corridor") {
        const fresh = buildSeed();
        nodes.clear();
        edges.clear();
        fresh.nodes.forEach((v, k) => nodes.set(k, v));
        fresh.edges.forEach((v, k) => edges.set(k, v));
        centrePair = undefined;
      }
      break;
    }

    case "catalogue.LinkOpened": {
      // The candidate edge between Aurora and Al Madar appears, dashed.
      upsertEdge("aurora<->almadar", {
        kind: "counterparty",
        from: "orbid:5493-aves-aurora-br",
        to: "orbid:5493-almadar-ae",
        state: "cold",
        weight: 0.18,
        candidate: true,
      });
      break;
    }

    case "identity.OrbIDIssued": {
      const subject = ev.payload.orbid as string | undefined;
      if (subject) {
        igniteNode(subject, "verified", "verify");
        // The candidate edge to Aurora becomes solid the moment the buyer's
        // OrbID mints (the demo treats this as "the network has a hub").
        const e = edges.get("aurora<->almadar");
        if (e) {
          edges.set("aurora<->almadar", { ...e, state: "verified", weight: 0.45, candidate: false });
        }
      }
      // GLEIF cluster ignites alongside the LEI sub-credential.
      igniteNode("gleif", "verified", "verify");
      break;
    }

    case "trade.ProposalAccepted": {
      // Couple the two sides into a verified counterparty edge. We resolve
      // the buyer↔seller pair from the saga correlationId (scenario id) since
      // the proposal flow always involves the same canonical pair.
      const corr = ev.correlationId ?? "";
      if (corr === "t1-onboarding" || corr === "t2-bank-financed") {
        upsertEdge("aurora<->almadar", {
          kind: "counterparty",
          from: "orbid:5493-aves-aurora-br",
          to: "orbid:5493-almadar-ae",
          state: "verified",
          weight: 0.55,
          candidate: false,
        });
      } else if (corr === "t3-reverse-corridor") {
        upsertEdge("pampagro<->gulfphosphate", {
          kind: "counterparty",
          from: "orbid:5493-pampagro-br",
          to: "orbid:5493-gulfphosphate-ae",
          state: "verified",
          weight: 0.55,
          candidate: false,
        });
        igniteNode("orbid:5493-pampagro-br", "verified", "verify");
        igniteNode("orbid:5493-gulfphosphate-ae", "verified", "verify");
      }
      break;
    }

    case "document.Attested": {
      const attestor = ev.payload.attestorOrbid as string | undefined;
      if (attestor) igniteNode(attestor, "traced", "trace");
      // Add an HCS anchor diamond to the active counterparty edge.
      const corr = ev.correlationId ?? "";
      const edgeKey = corr === "t3-reverse-corridor" ? "pampagro<->gulfphosphate" : "aurora<->almadar";
      const e = edges.get(edgeKey);
      if (e) {
        edges.set(edgeKey, {
          ...e,
          state: "traced",
          weight: Math.max(e.weight, 0.62),
          hcsAnchors: [
            ...e.hcsAnchors,
            {
              id: `${edgeKey}-anchor-${e.hcsAnchors.length}`,
              along: 0.20 + 0.10 * (e.hcsAnchors.length % 6),
              emittedAt: now,
            },
          ],
        });
      }
      break;
    }

    case "document.BatchAttested": {
      // T2/T3 compressed montage emits a single batch event for all six docs.
      const corr = ev.correlationId ?? "";
      const edgeKey = corr === "t3-reverse-corridor" ? "pampagro<->gulfphosphate" : "aurora<->almadar";
      const partners = (ev.payload.attestorOrbids as string[] | undefined) ?? [];
      partners.forEach((id) => igniteNode(id, "traced", "trace"));
      const e = edges.get(edgeKey);
      if (e) {
        const anchors = Array.from({ length: 6 }, (_, i) => ({
          id: `${edgeKey}-batch-${e.hcsAnchors.length + i}`,
          along: 0.18 + i * 0.11,
          emittedAt: now + i * 60,
        }));
        edges.set(edgeKey, {
          ...e,
          state: "traced",
          weight: 0.7,
          hcsAnchors: [...e.hcsAnchors, ...anchors],
        });
      }
      break;
    }

    case "lc.Issued": {
      const corr = ev.correlationId ?? "";
      const buyerSeller = corr === "t3-reverse-corridor"
        ? { buyer: "orbid:5493-pampagro-br", seller: "orbid:5493-gulfphosphate-ae" }
        : { buyer: "orbid:5493-almadar-ae",   seller: "orbid:5493-aves-aurora-br" };

      const lcRef = ev.payload.lcReference as string | undefined;
      const bankBadge = ev.payload.buyerSideBankBadge as ("FAB" | "Itau" | null | undefined);
      const fundingMix = ev.payload.fundingMix as { bankFinancingPct?: number } | undefined;
      const fundingPct = fundingMix?.bankFinancingPct;

      // Ignite the counterparty edge to "financed" — Finance pillar accent.
      const edgeKey = corr === "t3-reverse-corridor" ? "pampagro<->gulfphosphate" : "aurora<->almadar";
      const e = edges.get(edgeKey);
      if (e) {
        edges.set(edgeKey, {
          ...e,
          state: "financed",
          weight: 0.85,
          bankName: bankBadge ?? undefined,
          bankFundingPct: bankBadge ? fundingPct : undefined,
        });
      }

      // Materialise an LcNode between buyer and seller.
      if (lcRef) {
        upsertNode(lcRef, {
          kind: "LcNode",
          state: "financed",
          accent: "finance",
          label: lcRef,
        });
      }

      // If this is a bank-financed trade, materialise the Bank node and
      // anchor it adjacent to the buyer side.
      if (bankBadge) {
        const bankId = `bank:${bankBadge}`;
        upsertNode(bankId, {
          kind: "Bank",
          state: "financed",
          accent: "finance",
          label: bankBadge === "FAB" ? "First Abu Dhabi Bank" : "Itaú Unibanco",
          bankName: bankBadge,
          flag: bankBadge === "FAB" ? "AE" : "BR",
        });
        // Bank ↔ buyer edge with the bank badge annotation.
        upsertEdge(`${bankId}->${buyerSeller.buyer}`, {
          kind: "lc",
          from: bankId,
          to: buyerSeller.buyer,
          state: "financed",
          weight: 0.55,
          bankName: bankBadge,
          bankFundingPct: fundingPct,
        });
      }
      break;
    }

    case "settlement.EventReceived":
      // Settlement vendors are deliberately hidden — no node materialises
      // here. The edge already glows financed; we just refresh the pulse.
      {
        const corr = ev.correlationId ?? "";
        const edgeKey = corr === "t3-reverse-corridor" ? "pampagro<->gulfphosphate" : "aurora<->almadar";
        const e = edges.get(edgeKey);
        if (e) {
          edges.set(edgeKey, {
            ...e,
            hcsAnchors: [
              ...e.hcsAnchors,
              { id: `${edgeKey}-settle-${now}`, along: 0.65 + Math.random() * 0.2, emittedAt: now },
            ],
          });
        }
      }
      break;

    case "ui.WebRotated": {
      const pair = ev.payload.centreOnPair as [string, string] | undefined;
      if (pair) centrePair = pair;
      break;
    }

    case "ui.WebTakeoverEntered":
      fullBleed = true;
      break;
    case "ui.WebTakeoverExited":
      fullBleed = false;
      break;

    default:
      // every other CDM event is irrelevant to graph topology
      break;
  }

  return {
    nodes,
    edges,
    centrePair,
    fullBleed,
    updatedAt: now,
  };
}

/**
 * Apply a stream of events to the cold-state seed. Useful for replays and
 * for SSR hydration when an event log already exists.
 */
export function buildGraphState(events: CdmEvent[]): GraphState {
  return events.reduce(
    (state, ev) => applyEvent(state, ev, new Date(ev.occurredAt).getTime()),
    buildSeed(),
  );
}
